/**
 * ...license header unchanged...
 */

import { useState, useEffect } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import MDBadge from "components/MDBadge";
import MDInput from "components/MDInput";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faStar } from "@fortawesome/free-solid-svg-icons";

import { Link } from "react-router-dom";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import Icon from "@mui/material/Icon";

import TicketNote from "./ticketNote";
import TicketCorrection from "./ticketCorrection";

import { useAuthContext } from "../../../hooks/useAuthContext";
import { httpGet } from "../../../api";
import { formatDate, DownloadFile } from "../../../utils";

// ── User type filter options ──────────────────────────────────────────────────
const USER_TYPE_OPTIONS = [
  { label: "All", value: "all" },
  { label: "EXPORT",  value: "E"  },
  { label: "LOCAL",    value: "L"    },
];

const Tables = (props) => {
  const endPoint = props.all ? "/ticket/all" : "/ticket/my";
  const { user } = useAuthContext();

  const columns = [
    { Header: "ID",            accessor: "id",          align: "center" },
    { Header: "Creation Date", accessor: "date",         align: "center" },
    { Header: "Title",         accessor: "title",        align: "left"   },
    { Header: "Description",   accessor: "description",  align: "left"   },
    { Header: "Status",        accessor: "status",       align: "center" },
    { Header: "Note",          accessor: "note",         align: "center" },
    { Header: "action",        accessor: "action",       align: "center" },
  ];

  const [rows, setRows]               = useState([]);
  const [TableData, setTableData]     = useState([]);
  const [refrechedData, setRefrechedData] = useState(true);
  const [currentTicket, setCurrentTicket] = useState(null);
  const [openNote, setOpenNote]           = useState(false);
  const [openCorrection, setOpenCorrection] = useState(false);

  // ── NEW: active user_type filter ──────────────────────────────────────────
  const [userTypeFilter, setUserTypeFilter] = useState("all");

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleOpenCorrection = (id) => { setCurrentTicket(id); setOpenCorrection(true); };
  const handleCloseCorrection = () => { setCurrentTicket(null); setOpenCorrection(false); };

  const handleDownloadCorrection = async (id) => {
    const response = await httpGet(process.env.REACT_APP_URL_BASE + "/correction/" + id);
    if (response.status !== 200) return;

    const correction = await response.json();
    const exported = {
      "Reference":           correction.id,
      "Ticket Date":         formatDate(new Date(correction.ticket_date_created)),
      "Client":              correction.first_name + " " + correction.second_name,
      "Title":               correction.title,
      "Description":         correction.description,
      "Status":              correction.status,
      "Client Evaluation":   correction.note,
      "Possible Cause(s)":   correction.cause,
      "Action Taken":        correction.action_todo,
      "Corrective Action":   correction.action_done,
      "Follow up (12 months)": Boolean(correction.follow_12) ? "YES" : "NO",
      "Follow up (24 months)": Boolean(correction.follow_24) ? "YES" : "NO",
      "Date of correction":  formatDate(new Date(correction.date_created)),
    };

    DownloadFile({ "1": [exported] }, "correction_ticket_" + id);
  };

  const handleOpenNote  = (id) => { setCurrentTicket(id); setOpenNote(true); };
  const handleCloseNote = ()   => { setCurrentTicket(null); setOpenNote(false); };

  // ── Export ────────────────────────────────────────────────────────────────
  const handleExport = async () => {
    const response = await httpGet(process.env.REACT_APP_URL_BASE + "/ticket/export/");
    console.log("Export response status:", response.status);

    if (response.status !== 200) {
      alert("Export failed");
      return;
    }

    const data = await response.json();

    console.log("Export data:", data);

    const rows = data.map((item) => ({
      "référence":                          item.id,
      "Mois":                               item.month,
      "Structure":                          item.struct,
      "Client":                             item.full_name,
      "Catégorie":                          "",           // TODO
      "Titre":                              item.title,
      "Status":                             item.status,
      "Date / heure":                       item.full_date,
      "Demande":                            item.description,
      "Appréciation du degré de gravité":   "",           // TODO
      "Cause(s) possible(s)":               item.cause,
      "Correction de l'action":             item.action_todo,
      "Satisfaction du client":             item.note_label,
      "Action corrective":                  item.action_done,
      "Suivi":                              "",           // TODO
    }));

    DownloadFile({ "Tickets": rows }, `tickets_export_${new Date().toISOString().slice(0, 10)}`);
  };

  // ── Fetch ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      const response = await httpGet(process.env.REACT_APP_URL_BASE + endPoint);
      if (response.status !== 200) return;
      setTableData(await response.json());
      setRefrechedData(false);           // reset flag after load
    };
    if (refrechedData) fetchData();
  }, [refrechedData]);

  // ── Build rows (with user_type filter applied) ───────────────────────────
  useEffect(() => {
    // 1. Filter the raw data first
    const filtered = userTypeFilter === "all"
      ? TableData
      : TableData.filter((row) => row.user_type === userTypeFilter);

    // 2. Map to table rows (unchanged logic)
    setRows(
      filtered.map((row) => {
        const actions = [
          <Link to={`/tickets/${row.id}`}>
            <MDButton
              variant="outlined"
              color={row.evaluations_done === row.evaluations_total ? "success" : "warning"}
              size="small"
            >
              View
            </MDButton>
          </Link>,
        ];

        if (
          (row.status === "closed" || row.status === "treated") &&
          user.role === "user" &&
          row.note == null
        ) {
          actions.push(
            <MDButton
              variant="outlined"
              color="info"
              size="small"
              sx={{ marginLeft: ".5em" }}
              onClick={() => handleOpenNote(row.id)}
            >
              Evaluate
            </MDButton>
          );
        }

        if ((row.status === "closed" || row.status === "treated") && user.role === "tech") {
          if (row.correction_done === null) {
            actions.push(
              <MDButton
                variant="outlined"
                color="info"
                size="small"
                sx={{ marginLeft: ".5em" }}
                onClick={() => handleOpenCorrection(row.id)}
              >
                Correction
              </MDButton>
            );
          } else {
            actions.push(
              <MDButton
                variant="outlined"
                color="warning"
                size="small"
                sx={{ marginLeft: ".5em" }}
                onClick={() => handleDownloadCorrection(row.id)}
              >
                <Icon fontSize="large">download</Icon> Correction
              </MDButton>
            );
          }
        }

        const statusColor = { open: "info", closed: "success", treated: "success", progress: "warning" };
        const stars = Array.from({ length: 5 }, (_, i) => (
          <FontAwesomeIcon
            key={i}
            icon={faStar}
            style={{ color: i < row.note ? "#FFD43B" : "#C1C1C1" }}
          />
        ));

        return {
          id:          row.id,
          title:       row.title,
          date:        formatDate(new Date(row.date_created)),
          description: row.description,
          status:      <MDBadge color={statusColor[row.status]} badgeContent={row.status} variant="gradient" size="lg" container />,
          note:        stars,
          action:      actions,
        };
      })
    );
  }, [TableData, userTypeFilter]);  // ← re-runs whenever filter changes

  return (
    <DashboardLayout>
      <TicketNote ticket={currentTicket} open={openNote} close={handleCloseNote} setRefreshed={setRefrechedData} />
      <TicketCorrection ticket={currentTicket} open={openCorrection} close={handleCloseCorrection} setRefreshed={setRefrechedData} />

      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2} mt={-3} py={3} px={2}
                variant="gradient" bgColor="error"
                borderRadius="lg" coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  List of tickets
                </MDTypography>
              </MDBox>

              {/* ── User Type Filter Bar ─────────────────────────────────── */}
              {user.role !== "user" && (
                <>
                  <MDBox px={3} pt={3} display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <MDTypography variant="caption" fontWeight="medium" color="text" sx={{ mr: 1 }}>
                      Filter by ticket type:
                    </MDTypography>
                    {USER_TYPE_OPTIONS.map((opt) => (
                      <MDButton
                        key={opt.value}
                        variant={userTypeFilter === opt.value ? "contained" : "outlined"}
                        color="error"
                        size="small"
                        onClick={() => setUserTypeFilter(opt.value)}
                      >
                        {opt.label}
                      </MDButton>
                    ))}
                  </MDBox>

                  {/* ── Export Button ──────────────────────────────────────────── */}
                  <MDBox sx={{ marginLeft: "auto", marginRight: "1em" }}>
                    <MDButton
                      variant="outlined"
                      color="success"
                      size="small"
                      onClick={handleExport}
                    >
                      <Icon fontSize="small" sx={{ mr: 0.5 }}>download</Icon>
                      Excel
                    </MDButton>
                  </MDBox>
                </>
              )}
              {/* ──────────────────────────────────────────────────────────── */}

              <MDBox pt={2}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={true}
                  entriesPerPage={true}
                  showTotalEntries={true}
                  noEndBorder
                  canSearch
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
};

export default Tables;